import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Reveal } from '@/components/generals/reveal'
import { SectionLabel } from '@/components/generals/section-label'
import { aboutStats } from '@/constants/content'

export const About: FC = (): ReactNode => {
    return (
        <section id='about' className='overflow-hidden border-t border-foreground/10 py-20 md:py-32'>
            <div className='page-container grid gap-16 md:grid-cols-12 md:gap-8'>
                <div className='relative mx-auto w-full max-w-md md:col-span-5 md:max-w-none'>
                    <Reveal>
                        <div className='relative aspect-[4/5] overflow-hidden rounded-2xl bg-muted'>
                            <Image
                                src='/images/nico-portrait.jpg'
                                alt="Portrait of Nico Varela smiling next to a wall of sticky notes"
                                fill
                                sizes='(min-width: 768px) 40vw, 100vw'
                                className='object-cover object-[center_20%]'
                            />
                        </div>
                    </Reveal>

                    <div className='absolute -bottom-10 -left-2 w-[44%] -rotate-6 rounded-xl bg-card p-2 pb-3 shadow-[0_30px_60px_-30px_rgba(18,17,16,0.55)] md:-left-8'>
                        <div className='relative aspect-square overflow-hidden rounded-lg bg-muted'>
                            <Image
                                src='/images/hero-lisbon.jpg'
                                alt="Pastel houses and terracotta rooftops in Lisbon"
                                fill
                                sizes='220px'
                                className='object-cover'
                            />
                        </div>

                        <p className='mt-2 text-center font-serif text-lg italic leading-none'>
                            Home base, Lisbon
                        </p>
                    </div>

                    <p className='absolute -right-2 top-6 rotate-[8deg] rounded-full bg-brand px-5 py-2.5 font-heading text-lg font-semibold text-brand-foreground shadow-[0_18px_40px_-16px_rgba(43,53,255,0.8)] md:-right-6'>
                        Hi, I'm Nico!
                    </p>
                </div>

                <div className='md:col-span-6 md:col-start-7'>
                    <SectionLabel className='text-muted-foreground'>
                        About
                    </SectionLabel>

                    <p className='mt-5 text-balance font-heading text-[clamp(2rem,3.6vw,3.4rem)] font-semibold leading-[1.02] tracking-[-0.035em]'>
                        For nine years I've helped brands trade <em className='font-serif font-normal italic tracking-[-0.01em] text-brand'>forgettable</em> for impossible to ignore.
                    </p>

                    <div className='mt-8 space-y-5 text-lg leading-relaxed text-foreground/75'>
                        <p>
                            I started out designing club flyers in Porto, spent three years at agencies in Amsterdam and London, and went independent in 2020 to work closer to the people I design for.
                        </p>

                        <p>
                            Today I partner with founders, studios and marketing teams who want a website with a point of view. I design and build everything myself, so nothing gets lost between the mockup and the launch, and you always talk to the person doing the work.
                        </p>
                    </div>

                    <dl className='mt-12 grid grid-cols-3 gap-4 border-t border-foreground/15 pt-8'>
                        {aboutStats.map(stat => (
                            <div key={stat.label} className='flex flex-col-reverse gap-2'>
                                <dt className='text-sm leading-snug text-muted-foreground'>
                                    {stat.label}
                                </dt>

                                <dd className='font-heading text-4xl font-semibold tracking-[-0.045em] md:text-6xl'>
                                    {stat.value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>
        </section>
    )
}
